<script setup lang="ts">
import { computed } from 'vue';
import { FunnelIcon } from '@heroicons/vue/24/outline';
import { useI18n } from 'vue-i18n';
import type { MenuCategory } from '@/types/menu';
import SearchBar from '@/components/ui/SearchBar.vue';
import BaseFilterButtons from '@/components/common/BaseFilterButtons.vue';

interface Props {
  categories: MenuCategory[];
  selectedCategoryId: number | null;
  searchQuery: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  filterCategory: [categoryId: number | null];
  searchChange: [query: string];
}>();

const { t } = useI18n();

// Category options converted to filter group format
const categoryOptions = computed(() => 
  props.categories.map(category => ({
    value: category.id,
    label: category.name
  }))
);

// Filter groups for BaseFilterButtons
const filterGroups = computed(() => [
  {
    name: 'category',
    options: categoryOptions.value,
    activeClass: 'bg-indigo-600 text-white',
    allowToggle: true
  }
]);

// Current filter values
const filterValues = computed(() => ({
  category: props.selectedCategoryId || undefined
}));

// Event handlers
const handleFilterClick = (groupName: string, value: string | number | undefined) => {
  if (groupName === 'category') {
    emit('filterCategory', value !== undefined ? Number(value) : null);
  }
};
</script>

<template>
  <div class="mt-3 sm:mt-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
    <!-- Category Filters Section -->
    <div class="flex-1">
      <div class="flex items-center gap-2 mb-3 sm:mb-2">
        <FunnelIcon class="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 dark:text-gray-500" />
        <span class="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
          {{ t('app.views.menu.list.filter_by_category') }}
        </span>
      </div>
      
      <BaseFilterButtons
        :filter-groups="filterGroups"
        :filter-values="filterValues"
        @filter-click="handleFilterClick"
      />
    </div>

    <!-- Search Bar -->
    <div class="w-full sm:max-w-xs sm:mt-6">
      <SearchBar
        :model-value="searchQuery"
        :placeholder="t('app.actions.search') || 'Buscar productos'"
        size="md"
        input-id="menu-search"
        @update:model-value="emit('searchChange', $event)"
      />
    </div>
  </div>
</template>
